import feesService from '@/services/fees';
import {
  extractApiErrorMessage,
  extractApiSuccessMessage,
  extractPayload,
  resolveApiPayload,
} from '@/utils/dataNormalization';
import { formatCurrency } from '@/utils/numberFormat';
import { buildFeeStoreRequestData } from '@/composables/fees/useFeeModalForm';
import { FEES_TEXT } from '@/texts/catalog/fees';

const FLOW_TEXT = FEES_TEXT.flow;
const CONFIRM_TEXT = FEES_TEXT?.page?.confirm || {};

const normalizeMode = (mode = 'create') => {
  return String(mode || '').trim().toLowerCase() === 'edit'
    ? 'edit'
    : 'create';
};

export const openFeeModalState = (vm, mode = 'create', initialData = null) => {
  vm.feeModalMode = normalizeMode(mode);
  vm.feeModalInitialData = initialData && typeof initialData === 'object'
    ? { ...initialData }
    : null;
  vm.isFeeModalOpen = true;
};

export const closeFeeModalState = (vm) => {
  if (vm.isFeeSubmitting) {
    return;
  }

  vm.isFeeModalOpen = false;
  vm.feeModalMode = 'create';
  vm.feeModalInitialData = null;
};

export const submitFeeFlow = async (
  vm,
  payload,
  {
    saveErrorMessage = FLOW_TEXT.errors.save,
    createSuccessMessage = FLOW_TEXT.success.create,
    updateSuccessMessage = FLOW_TEXT.success.update,
    mapRequestData = null,
    onSuccess = null,
  } = {}
) => {
  vm.isFeeSubmitting = true;

  try {
    const requestData = buildFeeStoreRequestData(payload);
    if (typeof mapRequestData === 'function') {
      mapRequestData(requestData, { vm, payload });
    }

    const isEditMode = normalizeMode(vm.feeModalMode) === 'edit';
    const feeId = Number(vm.feeModalInitialData?.id || 0);
    const response = isEditMode && feeId > 0
      ? await feesService.update(feeId, requestData)
      : await feesService.store(requestData);

    const apiErrorMessage = extractApiErrorMessage(response, saveErrorMessage);
    if (apiErrorMessage) {
      vm.$toast?.error(apiErrorMessage);
      return;
    }

    const responsePayload = resolveApiPayload(response);
    const responseType = String(responsePayload?.type || '').trim().toLowerCase();
    if (responseType !== '' && responseType !== 'success') {
      vm.$toast?.error(saveErrorMessage);
      return;
    }

    vm.$toast?.success(extractApiSuccessMessage(
      response,
      isEditMode ? updateSuccessMessage : createSuccessMessage,
    ));

    vm.isFeeModalOpen = false;
    vm.feeModalMode = 'create';
    vm.feeModalInitialData = null;

    if (typeof onSuccess === 'function') {
      await onSuccess({ vm, payload, requestData, response, isEditMode });
    }
  } catch (error) {
    const errorPayload = error?.response?.data || error?.data || error;
    const apiErrorMessage = extractApiErrorMessage(errorPayload, '');
    const message = apiErrorMessage
      || String(errorPayload?.message || '').trim()
      || String(error?.message || '').trim()
      || saveErrorMessage;
    vm.$toast?.error(message);
  } finally {
    vm.isFeeSubmitting = false;
  }
};

export const openFeeEditModalFromRow = async (
  vm,
  row,
  {
    detailErrorMessage = FLOW_TEXT.errors.detail,
    mapInitialData = null,
  } = {}
) => {
  const feeId = Number(row?.id || 0);
  if (!feeId) {
    return;
  }

  try {
    const response = await feesService.show(feeId, {}, { skipLoading: true });
    const payload = extractPayload(response);
    const detail = payload && typeof payload === 'object' ? payload : {};
    const rowData = row && typeof row === 'object' ? row : {};

    const initialData = typeof mapInitialData === 'function'
      ? mapInitialData({ detail, rowData, payload })
      : (detail && Object.keys(detail).length > 0 ? detail : rowData);

    openFeeModalState(vm, 'edit', initialData);
  } catch (error) {
    vm.$toast?.error(detailErrorMessage);
  }
};

export const notifyFeeActionPending = (
  vm,
  row,
  {
    actionLabel = '',
    referencePrefix = FLOW_TEXT.pending.referencePrefix,
    suffix = FLOW_TEXT.pending.suffix,
  } = {}
) => {
  const reference = String(row?.reference || '').trim() || `${referencePrefix} #${row?.id || '-'}`;
  const action = String(actionLabel || '').trim();
  const message = action === ''
    ? `${reference} ${suffix}`
    : `${action} ${reference} ${suffix}`;

  vm.$toast?.info(message.trim());
};

export const resolveFeeApprovalTargetState = (row, explicitState = null) => {
  if (typeof explicitState === 'boolean') {
    return explicitState;
  }

  const approved = row?.is_approved;
  if (approved === true) {
    return false;
  }

  if (approved === false) {
    return true;
  }

  if (approved === undefined || approved === null || approved === '') {
    return true;
  }

  return Number(approved) !== 1;
};

export const resolveFeeApproveModalContent = ({
  row = null,
  approveTargetState = true,
  isApproving = false,
  confirmText = CONFIRM_TEXT,
} = {}) => {
  const source = confirmText && typeof confirmText === 'object' ? confirmText : {};
  const approveCopy = source?.approve && typeof source.approve === 'object' ? source.approve : {};

  let message = approveTargetState
    ? String(approveCopy?.messageApproveDefault || '').trim()
    : String(approveCopy?.messageUnapproveDefault || '').trim();

  if (row && typeof row === 'object') {
    const provider = String(row?.provider || row?.collaborator || '').trim() || String(source?.noProvider || '').trim();
    const concept = String(row?.concept || '').trim() || String(source?.noConcept || '').trim();
    const amount = formatCurrency(row?.total ?? row?.amount ?? 0, {
      currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
    });

    const summaryResolver = source?.summary;
    message = typeof summaryResolver === 'function'
      ? String(summaryResolver({ provider, concept, amount }) || '').trim()
      : '';
  }

  const confirmActionText = isApproving
    ? (approveTargetState
      ? String(approveCopy?.confirmingApprove || '').trim()
      : String(approveCopy?.confirmingUpdate || '').trim())
    : (approveTargetState
      ? String(approveCopy?.confirmApprove || '').trim()
      : String(approveCopy?.confirmUnapprove || '').trim());

  return {
    title: approveTargetState
      ? String(approveCopy?.titleApprove || '').trim()
      : String(approveCopy?.titleUnapprove || '').trim(),
    subtitle: approveTargetState
      ? String(approveCopy?.subtitleApprove || '').trim()
      : String(approveCopy?.subtitleUnapprove || '').trim(),
    message,
    confirmText: confirmActionText,
    cancelText: String(source?.cancel || '').trim(),
  };
};

export const resolveFeeDeleteModalContent = ({
  row = null,
  isDeleting = false,
  confirmText = CONFIRM_TEXT,
} = {}) => {
  const source = confirmText && typeof confirmText === 'object' ? confirmText : {};
  const deleteCopy = source?.delete && typeof source.delete === 'object' ? source.delete : {};

  let message = String(deleteCopy?.messageDefault || '').trim();

  if (row && typeof row === 'object') {
    const provider = String(row?.provider || row?.collaborator || '').trim() || String(source?.noProvider || '').trim();
    const concept = String(row?.concept || '').trim() || String(source?.noConcept || '').trim();
    const amount = formatCurrency(row?.total ?? row?.amount ?? 0, {
      currency: String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN',
    });

    const summaryResolver = source?.summary;
    if (typeof summaryResolver === 'function') {
      message = String(summaryResolver({ provider, concept, amount }) || '').trim();
    }
  }

  return {
    title: String(deleteCopy?.title || '').trim(),
    subtitle: String(deleteCopy?.subtitle || '').trim(),
    message,
    confirmText: isDeleting
      ? String(deleteCopy?.confirming || '').trim()
      : String(deleteCopy?.confirm || '').trim(),
    cancelText: String(source?.cancel || '').trim(),
  };
};

export const submitFeePayFlow = async (
  vm,
  payload = {},
  {
    errorIdentifyingMessage = FLOW_TEXT.errors.payIdentify,
    errorSaveMessage = FLOW_TEXT.errors.paySave,
    successMessage = FLOW_TEXT.success.pay,
    onSuccess = null,
  } = {}
) => {
  if (vm.isPayFeeSubmitting) {
    return;
  }

  const feeId = Number(
    vm.payFeeTarget?.id
    || payload?.fee_id
    || 0
  ) || 0;

  if (feeId <= 0) {
    vm.$toast?.error(errorIdentifyingMessage);
    return;
  }

  vm.isPayFeeSubmitting = true;

  try {
    const response = await feesService.pay(feeId, payload || {});
    const apiErrorMessage = extractApiErrorMessage(response, errorSaveMessage);
    if (apiErrorMessage) {
      vm.$toast?.error(apiErrorMessage);
      return;
    }

    const responsePayload = resolveApiPayload(response);
    const responseType = String(responsePayload?.type || '').trim().toLowerCase();
    if (responseType !== '' && responseType !== 'success') {
      vm.$toast?.error(errorSaveMessage);
      return;
    }

    vm.$toast?.success(extractApiSuccessMessage(response, successMessage));
    vm.isPayFeeModalOpen = false;
    vm.payFeeTarget = null;

    if (typeof onSuccess === 'function') {
      await onSuccess({ vm, payload, response });
    }
  } catch (error) {
    const errorPayload = error?.response?.data || error?.data || error;
    const apiErrorMessage = extractApiErrorMessage(errorPayload, '');
    const message = apiErrorMessage
      || String(errorPayload?.message || '').trim()
      || String(error?.message || '').trim()
      || errorSaveMessage;
    vm.$toast?.error(message);
  } finally {
    vm.isPayFeeSubmitting = false;
  }
};

export const confirmFeeDeleteFlow = async (
  vm,
  {
    errorMessage = FLOW_TEXT.errors.delete,
    successMessage = FLOW_TEXT.success.delete,
    onSuccess = null,
  } = {}
) => {
  if (vm.isDeletingFee) {
    return;
  }

  const feeId = Number(vm.deleteFeeTarget?.id || 0);
  if (feeId <= 0) {
    vm.isDeleteFeeModalOpen = false;
    vm.deleteFeeTarget = null;
    return;
  }

  vm.isDeletingFee = true;

  try {
    const response = await feesService.delete(feeId);
    const apiErrorMessage = extractApiErrorMessage(response, errorMessage);
    if (apiErrorMessage) {
      vm.$toast?.error(apiErrorMessage);
      return;
    }

    vm.$toast?.success(extractApiSuccessMessage(response, successMessage));
    vm.isDeleteFeeModalOpen = false;
    vm.deleteFeeTarget = null;

    if (typeof onSuccess === 'function') {
      await onSuccess({ vm, response, feeId });
    }
  } catch (error) {
    const message = String(error?.response?.data?.message || '').trim() || errorMessage;
    vm.$toast?.error(message);
  } finally {
    vm.isDeletingFee = false;
  }
};

export const confirmFeeApproveFlow = async (
  vm,
  {
    errorMessage = FLOW_TEXT.errors.approve,
    successApprovedMessage = FLOW_TEXT.success.approve,
    successUnapprovedMessage = FLOW_TEXT.success.unapprove,
    onSuccess = null,
  } = {}
) => {
  if (vm.isApprovingFee) {
    return;
  }

  const feeId = Number(vm.approveFeeTarget?.id || 0);
  if (feeId <= 0) {
    vm.isApproveFeeModalOpen = false;
    vm.approveFeeTarget = null;
    vm.approveFeeTargetState = true;
    return;
  }

  vm.isApprovingFee = true;

  try {
    const response = await feesService.approve(feeId, {
      approved: vm.approveFeeTargetState,
    });

    const apiErrorMessage = extractApiErrorMessage(response, errorMessage);
    if (apiErrorMessage) {
      vm.$toast?.error(apiErrorMessage);
      return;
    }

    vm.$toast?.success(extractApiSuccessMessage(
      response,
      vm.approveFeeTargetState ? successApprovedMessage : successUnapprovedMessage,
    ));
    vm.isApproveFeeModalOpen = false;
    vm.approveFeeTarget = null;
    vm.approveFeeTargetState = true;

    if (typeof onSuccess === 'function') {
      await onSuccess({ vm, response, feeId });
    }
  } catch (error) {
    const message = String(error?.response?.data?.message || '').trim() || errorMessage;
    vm.$toast?.error(message);
  } finally {
    vm.isApprovingFee = false;
  }
};
